// File: src/pages/UXInsightsPage.jsx
import React, {
  useEffect,
  useRef,
  useState,
  useContext,
  useMemo,
  useCallback
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import './HeatmapPage.css';
import LayoutWrapper from '../components/Layout/Layout';
import useData from '../hooks/useData';
import { 
  Page, 
  Layout, 
  Card, 
  Text, 
  Banner,
  SkeletonPage,
  SkeletonBodyText,
  Box,
  //Button,
  Tooltip,
  Icon
} from '@shopify/polaris';
import { 
  ArrowLeftIcon, 
  QuestionCircleIcon,
  CheckIcon,
  ChartHistogramFullIcon 
} from '@shopify/polaris-icons';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  Cell,
  // PieChart, Pie, Legend removed - replaced with behavioral insights
} from 'recharts';
import { formatPageLabel, cleanText } from '../utils/cleanText';
import PrescriptionBanner from '../components/Prescriptions/PrescriptionBanner';

const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';
const FN_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:8888' : '';

/*const MODES = [
  { label: 'Clicks', value: 'clicks' },
];*/

const REGIONS = [
  { key: 'top-left', top: '0%', left: '0%' },
  { key: 'top-middle', top: '0%', left: '33.333%' },
  { key: 'top-right', top: '0%', left: '66.666%' },
  { key: 'middle-left', top: '33.333%', left: '0%' },
  { key: 'middle-middle', top: '33.333%', left: '33.333%' },
  { key: 'middle-right', top: '33.333%', left: '66.666%' },
  { key: 'bottom-left', top: '66.666%', left: '0%' },
  { key: 'bottom-middle', top: '66.666%', left: '33.333%' },
  { key: 'bottom-right', top: '66.666%', left: '66.666%' },
];

function PageGoal({ goal }) {
  return (
    <Text as="p" tone="subdued">
      <Text as="span" fontWeight="bold">{cleanText("Page Goal:")}</Text> {cleanText(goal)}
    </Text>
  );
}

// Helper to describe elements more generically
function describeElement(selector) {
  if (!selector) return 'Unknown Element';
  const lower = selector.toLowerCase();

  if (lower.includes('add-to-cart') || lower.includes('addtocart')) return 'Add to Cart Button';
  if (lower.includes('checkout')) return 'Checkout Button';
  if (lower.includes('submit')) return 'Submit Button';
  if (lower.includes('search')) return 'Search Field/Button';
  if (lower.includes('menu') || lower.includes('nav')) return 'Navigation Menu';
  if (lower.includes('close') || lower.includes('dismiss')) return 'Close/Dismiss Button';
  if (lower.includes('filter')) return 'Filter Option';
  if (lower.includes('sort')) return 'Sort Option';
  if (lower.includes('quantity') || lower.includes('qty')) return 'Quantity Selector';
  if (lower.includes('variant') || lower.includes('size') || lower.includes('color')) return 'Product Variant Selector';
  if (lower.includes('email')) return 'Email Input Field';
  if (lower.includes('password')) return 'Password Field';
  if (lower.includes('login') || lower.includes('signin')) return 'Login/Sign In Button';
  if (lower.includes('register') || lower.includes('signup')) return 'Sign Up Button';
  if (lower.includes('cart')) return 'Cart Related Element';
  if (lower.includes('product')) return 'Product Element';
  if (lower.includes('image') || lower.includes('img')) return 'Product Image';
  if (lower.includes('button') || lower.includes('btn')) return 'Button';
  if (lower.includes('link') || lower.includes('href')) return 'Link';
  if (lower.includes('input') || lower.includes('field')) return 'Input Field';

  if (selector.startsWith('.')) {
    const className = selector.slice(1).replace(/[-_]/g, ' ');
    return className.length > 30 ? 'Page Element' : className;
  }
  if (selector.startsWith('#')) {
    const idName = selector.slice(1).replace(/[-_]/g, ' ');
    return idName.length > 30 ? 'Page Section' : idName;
  }

  return 'Page Element';
}

// Calculate revenue impact from rage clicks
/*const calculateRevenueImpact = (alerts, avgOrderValue = 75) => {
  if (!alerts || !Array.isArray(alerts)) return { loss: 0, percentage: 0, sessions: 0, conversionRisk: 0 };

  const totalRageClicks = alerts.reduce((sum, alert) => sum + (alert.count || 0), 0);
  const estimatedLostSessions = Math.round(totalRageClicks * 0.15);
  const potentialLostSales = estimatedLostSessions * 0.025;
  const estimatedRevenueLoss = Math.round(potentialLostSales * avgOrderValue);
  const sessionAbandonmentRate = 15;

  return {
    loss: estimatedRevenueLoss,
    percentage: Math.round(potentialLostSales * 100) / 100,
    sessions: estimatedLostSessions,
    conversionRisk: sessionAbandonmentRate,
  };
};*/

// Severity classification
const getSeverity = (count) => {
  if (count >= 50) return { level: 'critical', tone: 'critical', label: 'Critical' };
  if (count >= 20) return { level: 'warning', tone: 'warning', label: 'High' };
  if (count >= 10) return { level: 'attention', tone: 'attention', label: 'Medium' };
  return { level: 'info', tone: 'info', label: 'Low' };
};

// Priority score for sorting
const getPriorityScore = (alert) => {
  let score = alert.count || 0;
  if (alert.page_path === '/' || alert.page_path === '/cart' || alert.page_path.includes('checkout')) {
    score *= 2;
  }
  if (alert.element && (alert.element.includes('button') || alert.element.includes('form'))) {
    score *= 1.5;
  }
  return score;
};

// Custom tooltip for rage clicks chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload[0]) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}>
        <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
        <p style={{ margin: '4px 0', color: 'var(--accent)' }}>
          Rage Clicks: {payload[0].value}
        </p>
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--muted)' }}>
          Est. impact: ~{Math.round(payload[0].value * 0.15)} lost sessions
        </p>
      </div>
    );
  }
  return null;
};

export default function UXInsightsPage() {
  const { token } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  
  // Heatmap state
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const bgImageRef = useRef(null);
  const [bgOk, setBgOk] = useState(false);
  
  // State
  const [selectedSite, setSelectedSite] = useState(() => sessionStorage.getItem('site_id') || '');
  const [selectedPage, setSelectedPage] = useState('');
  const [mode] = useState('clicks');
  const [timeRange, setTimeRange] = useState(24);
  const [userType, setUserType] = useState('all');
  const [regionCounts, setRegionCounts] = useState({});
  const [hoverRegion, setHoverRegion] = useState(null);
  // FIX: Default showBg to false so heatmap shows immediately
  const [showBg, setShowBg] = useState(false);
  const [cacheBust, setCacheBust] = useState(() => Date.now());
  // Track rendered image dimensions for canvas alignment
  const [renderedImgSize, setRenderedImgSize] = useState({ width: 0, height: 0 });

  // Rage clicks state
  const threshold = 4;
  const windowSec = 10;
  const daysForAlerts = 30;

  // Derive siteId as a number for API calls that need it
  const siteId = selectedSite ? Number(selectedSite) : null;

  const isDev = process.env.NODE_ENV === 'development';
  const screenshotDebug = isDev ? '&debug=1' : '';

  // Normalize path helper
  const pathFromValue = (val) => {
    if (!val) return '/';
    if (/^https?:\/\//i.test(val)) { 
      try { 
        return new URL(val).pathname || '/'; 
      } catch { 
        return '/'; 
      } 
    }
    return String(val).split(/[?#]/)[0].replace(/^([^/])/, '/$1');
  };
  const normalizedPath = pathFromValue(selectedPage);

  // URL fallback effect
  useEffect(() => {
    if (!selectedSite) {
      const s = searchParams.get('site_id');
      if (s) { 
        setSelectedSite(s); 
        sessionStorage.setItem('site_id', s); 
      }
    }
  }, [searchParams, selectedSite]);

  // Refresh cache buster when inputs change
  useEffect(() => {
    setCacheBust(Date.now());
    setBgOk(false);
  }, [selectedSite, normalizedPath, timeRange, mode, userType]);

  // Fetch pages
  const { data: pages = [], loading: pagesLoading, error: pagesError } = useData(
    selectedSite ? `pages:${selectedSite}` : 'noop',
    () => fetch(`${API}/pages?site_id=${selectedSite}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (!res.ok) throw new Error(cleanText(`Status ${res.status}`));
      return res.json();
    }),
    { ttlMs: 60 * 60 * 1000, deps: [selectedSite, token] }
  );

  // Cache site host for screenshot
  useEffect(() => {
    if (!selectedSite) return;
    sessionStorage.removeItem('site_host');

    const first = (pages || []).find(p => {
      const path = typeof p === 'string' ? p : (p.page_url || p.url || p.full_url);
      return path && /^https?:\/\//i.test(path);
    });

    if (first) {
      const raw = typeof first === 'string' ? first : (first.page_url || first.url || first.full_url);
      try {
        const u = new URL(raw);
        sessionStorage.setItem('site_host', u.host);
        setBgOk(false);
      } catch {}
    }
  }, [pages, selectedSite]);

  // Fetch heatmap data
  const { data: heatmapData, loading: heatmapLoading, error: heatmapError } = useData(
    selectedSite ? `heatmap:${selectedSite}:${normalizedPath || 'all'}:${mode}:${timeRange}:${userType}` : 'noop',
    async () => {
      const params = new URLSearchParams({
        site_id: String(selectedSite),
        bucket_size: '50',
        hours: String(timeRange),
        mode,
        user_type: userType,
      });
      if (selectedPage) params.append('page_path', normalizedPath);

      const hm = await fetch(`${API}/analytics/heatmap?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!hm.ok) throw new Error(cleanText(`Status ${hm.status}`));
      return hm.json();
    },
    { ttlMs: 24 * 60 * 60 * 1000, deps: [selectedSite, normalizedPath, mode, timeRange, userType, token] }
  );

  const gridData = useMemo(() => heatmapData?.grid ?? [], [heatmapData]);
  const maxIntensity = useMemo(() => heatmapData?.max_intensity ?? 1, [heatmapData]);

  // Fetch rage clicks data
  const { data: allAlerts, loading: rageLoading, error: rageError } = useData(
    siteId ? `rageClicksAlerts:${siteId}:${daysForAlerts}` : 'noop',
    () => {
      if (siteId === null || isNaN(siteId) || !token) return Promise.resolve(null);
      const url = new URL(`${API}/analytics/rage-clicks`);
      url.searchParams.set('site_id', siteId.toString());
      url.searchParams.set('threshold', threshold.toString());
      url.searchParams.set('window_sec', windowSec.toString());
      url.searchParams.set('days', daysForAlerts.toString());

      return fetch(url.toString(), {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }).then((res) => {
        if (!res.ok) throw new Error(`Status ${res.status}`);
        return res.json();
      });
    },
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token, API, siteId] }
  );

  // ============================================================
  // NEW: Fetch behavioral insights (uses same endpoint as InsightsPage)
  // ============================================================
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const daysRange = '30'; // Match AppWarmup default

  const { data: insightsData, loading: insightsLoading, error: insightsError } = useData(
    siteId ? `insights:${siteId}:${daysRange}:${today}` : 'noop',
    () => {
      if (siteId === null || isNaN(siteId) || !token) return Promise.resolve(null);
      return fetch(`${API}/api/analytics/insights?site_id=${siteId}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }).then((res) => {
        if (!res.ok) throw new Error(`Status ${res.status}`);
        return res.json();
      });
    },
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token, API, siteId] }
  );

  // Extract behavioral_insights from the response
  // Note: The /api/analytics/insights endpoint returns { insights: "..." }
  // not { behavioral_insights: "..." }, so we need to check all possible keys
  const behavioralInsights = useMemo(() => {
    if (!insightsData) return null;
    return insightsData.behavioral_insights || insightsData.behavioral || insightsData.insights || null;
  }, [insightsData]);

  // Parse behavioral insights (handles new short format)
  const parsedBehavioralInsights = useMemo(() => {
    if (!behavioralInsights) return null;
    
    const source = String(behavioralInsights).trim();
    if (!source) return null;
    
    const result = {
      headline: '',
      context: '',
      stats: [],
      recommendations: [],
    };
    
    const lines = source.split('\n');
    
    // Line 1: **Headline**
    if (lines[0]) {
      result.headline = lines[0].replace(/\*\*/g, '').trim();
    }
    
    // Find context (first non-bullet, non-header line after headline)
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line && 
          !line.startsWith('-') && 
          !line.match(/^\d+\./) &&
          !line.toLowerCase().includes(':') &&
          !line.startsWith('**')) {
        result.context = line;
        break;
      }
    }
    
    // Parse bullets and numbered items
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('-')) {
        result.stats.push(trimmed.replace(/^-\s*/, ''));
      }
      if (trimmed.match(/^\d+\.\s/)) {
        result.recommendations.push(trimmed.replace(/^\d+\.\s*/, ''));
      }
    }
    
    return result;
  }, [behavioralInsights]);

  // Chart data for rage clicks
  const rageChartData = useMemo(() => {
    if (!allAlerts) return [];
    const countsByPage = {};
    allAlerts.forEach(({ page_path, count }) => {
      const page = formatPageLabel(page_path);
      countsByPage[page] = (countsByPage[page] || 0) + count;
    });
    return Object.entries(countsByPage)
      .map(([page, count]) => ({
        page,
        count,
        severity: getSeverity(count),
      }))
      .sort((a, b) => b.count - a.count);
  }, [allAlerts]);


  // Prioritized issues
  const prioritizedIssues = useMemo(() => {
    if (!allAlerts) return [];
    return [...allAlerts]
      .map((alert) => ({
        ...alert,
        score: getPriorityScore(alert),
        severity: getSeverity(alert.count),
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [allAlerts]);

  // Region counts for heatmap
  useEffect(() => {
    if (!gridData.length || mode === 'scroll') {
      setRegionCounts({});
      return;
    }
    const counts = {};
    const rows = gridData.length;
    const cols = gridData[0].length;
    const rowBlock = Math.floor(rows / 3);
    const colBlock = Math.floor(cols / 3);

    REGIONS.forEach(({ key }, idx) => {
      const blockRow = Math.floor(idx / 3);
      const blockCol = idx % 3;
      let sum = 0;
      for (let r = blockRow * rowBlock; r < (blockRow + 1) * rowBlock; r++) {
        for (let c = blockCol * colBlock; c < (blockCol + 1) * colBlock; c++) {
          if (gridData[r] && gridData[r][c] !== undefined) {
            sum += gridData[r][c] || 0;
          }
        }
      }
      counts[key] = sum;
    });

    setRegionCounts(counts);
  }, [gridData, mode]);

  // FIX: Updated redrawHeatmap to properly align with background image
  const redrawHeatmap = useCallback(() => {
    const overlay = overlayRef.current;
    const canvas = canvasRef.current;
    const bgImage = bgImageRef.current;
    
    if (!canvas || !gridData.length) return;

    const ctx = canvas.getContext('2d');
    const cols = gridData[0]?.length || 0;
    const rows = gridData.length;
    
    if (!rows || !cols) return;

    // Get container dimensions
    const containerW = overlay?.clientWidth || 600;
    const containerH = overlay?.clientHeight || 400;

    // Calculate the actual rendered size of the background image
    // When using object-fit: contain, the image scales to fit while preserving aspect ratio
    let renderW = containerW;
    let renderH = containerH;
    
    if (bgImage && bgImage.naturalWidth && bgImage.naturalHeight && showBg && bgOk) {
      const imgAspect = bgImage.naturalWidth / bgImage.naturalHeight;
      const containerAspect = containerW / containerH;
      
      if (imgAspect > containerAspect) {
        // Image is wider than container - width constrained
        renderW = containerW;
        renderH = containerW / imgAspect;
      } else {
        // Image is taller than container - height constrained
        renderH = containerH;
        renderW = containerH * imgAspect;
      }
      
      // Update state for positioning
      setRenderedImgSize({ width: renderW, height: renderH });
    } else {
      // No background - use container size
      setRenderedImgSize({ width: containerW, height: containerH });
    }
    
    if (!renderW || !renderH) return;

    // Set canvas to match the rendered image/container size
    const dpr = window.devicePixelRatio || 1;
    canvas.style.width = renderW + 'px';
    canvas.style.height = renderH + 'px';
    canvas.width = Math.round(renderW * dpr);
    canvas.height = Math.round(renderH * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.clearRect(0, 0, renderW, renderH);

    // Cell dimensions - grid maps 1:1 with the full page that screenshot captured
    const cellW = renderW / cols;
    const cellH = renderH / rows;
    const safeMax = maxIntensity > 0 ? maxIntensity : 1;

    // Draw heatmap cells with yellow-to-red gradient
    for (let r = 0; r < rows; r++) {
      const row = gridData[r] || [];
      for (let c = 0; c < cols; c++) {
        const v = row[c] || 0;
        if (!v) continue;
        
        const intensity = v / safeMax;
        const alpha = Math.min(0.8, intensity * 0.7 + 0.1);
        
        // Yellow to red gradient for better visibility
        const red = 255;
        const green = Math.round(255 * (1 - Math.pow(intensity, 0.6)));
        
        ctx.fillStyle = `rgba(${red},${green},0,${alpha})`;
        ctx.fillRect(c * cellW, r * cellH, cellW, cellH);
      }
    }
  }, [gridData, maxIntensity, showBg, bgOk]);

  // Effect to handle heatmap redrawing
  useEffect(() => {
    const bgImage = bgImageRef.current;
    let resizeTimeout;
    let initialDrawTimeout;
    
    const handleImageLoad = () => {
      redrawHeatmap();
    };

    if (bgImage) {
      bgImage.addEventListener('load', handleImageLoad);
    }
    
    // Draw immediately
    redrawHeatmap();
    
    // Also draw after a short delay to ensure DOM has rendered
    initialDrawTimeout = setTimeout(() => {
      redrawHeatmap();
    }, 100);
    
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        window.requestAnimationFrame(() => {
          redrawHeatmap();
        });
      }, 100);
    };

    const ro = new ResizeObserver(() => {
      handleResize();
    });
    
    try {
      if (overlayRef.current) ro.observe(overlayRef.current);
      if (bgImage) ro.observe(bgImage);
    } catch (e) {
      console.debug('ResizeObserver error:', e);
    }
    
    window.addEventListener('resize', handleResize);

    return () => {
      if (bgImage) bgImage.removeEventListener('load', handleImageLoad);
      clearTimeout(resizeTimeout);
      clearTimeout(initialDrawTimeout);
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [redrawHeatmap]);

  // Redraw when showBg changes
  useEffect(() => {
    // Small delay to let image render
    const timeout = setTimeout(() => {
      redrawHeatmap();
    }, 150);
    return () => clearTimeout(timeout);
  }, [showBg, bgOk, redrawHeatmap]);

  const formatRegionName = (name) => {
    return name
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const totalClicks = Object.values(regionCounts).reduce((a, b) => a + b, 0);
  const sorted = Object.entries(regionCounts || {}).sort(([, a], [, b]) => b - a);
  const totalRageClicks = allAlerts ? allAlerts.reduce((sum, a) => sum + a.count, 0) : 0;
  const barChartHeight = 400;

  // Quick wins for heatmap
  const quickWins = useMemo(() => {
    const [topRegion] = sorted[0] || ['', 0];
    return [
      {
        id: 'cta-placement',
        label: cleanText(`Move "Contact Us" button to ${formatRegionName(topRegion) || 'high-click area'}`),
        impact: 'high'
      },
      {
        id: 'social-proof',
        label: cleanText('Add customer testimonials in high-engagement zones'),
        impact: 'high'
      },
      {
        id: 'reduce-clutter',
        label: cleanText('Remove or minimize content in low-click areas'),
        impact: 'medium'
      },
      {
        id: 'mobile-test',
        label: cleanText('Test this heatmap on mobile vs desktop'),
        impact: 'high'
      },
      {
        id: 'offer-placement',
        label: cleanText('Place limited-time offers in top 3 click zones'),
        impact: 'high'
      }
    ];
  }, [sorted]);

  // Industry benchmarks
  const benchmarkData = useMemo(() => {
    const benchmarks = {
      'E-commerce': { topRegion: 45 },
      'Service Business': { topRegion: 38 },
      'B2B': { topRegion: 42 },
      'Blog/Content': { topRegion: 35 }
    };
    const topPercent = sorted[0] ? (sorted[0][1] / totalClicks) * 100 : 0;
    return { benchmarks, topPercent };
  }, [sorted, totalClicks]);

  const loading = pagesLoading || heatmapLoading || rageLoading;
  const error = pagesError || heatmapError || rageError;

  if (!token) {
    return (
      <LayoutWrapper>
        <Page title={cleanText("UX Insights")}>
          <Layout>
            <Layout.Section>
              <Banner status="critical">{cleanText("Authentication Required")}</Banner>
            </Layout.Section>
          </Layout>
        </Page>
      </LayoutWrapper>
    );
  }

  if (loading) {
    return (
      <LayoutWrapper>
        <Page title={cleanText("UX Insights")}>
          <SkeletonPage title={cleanText("UX Insights")}>
            <SkeletonBodyText />
          </SkeletonPage>
        </Page>
      </LayoutWrapper>
    );
  }

  if (error || !selectedSite) {
    return (
      <LayoutWrapper>
        <Page title={cleanText("UX Insights")}>
          <Layout>
            <Layout.Section>
              <Banner status="critical">
                {error ? cleanText(`Error: ${error.message || error}`) : cleanText('No site selected')}
              </Banner>
            </Layout.Section>
          </Layout>
        </Page>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <Page
        title={cleanText("UX Insights")}
        backAction={{
          content: cleanText('Back'),
          onAction: () => window.history.back(),
          icon: ArrowLeftIcon,
        }}
      >
        <PageGoal goal={cleanText("Identify friction points and engagement patterns to optimize user experience and boost conversions.")} />
        
        <div style={{
          height: 4, width: '100%', borderRadius: 4, margin: '4px 0 12px',
          background: 'linear-gradient(90deg, var(--accent), var(--accent-2))'
        }} />

        <PrescriptionBanner 
          siteId={siteId}
          token={token}
          category="traffic"
        />

        <Layout>
          {/* SECTION 1: KEY UX METRICS */}
          <Layout.Section>
            <Card title={cleanText("UX Health Overview")} sectioned>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <div style={{
                  padding: 20,
                  borderRadius: 12,
                  background: 'rgba(106, 13, 173, 0.1)',
                  border: '1px solid rgba(106, 13, 173, 0.3)'
                }}>
                  <div style={{ fontSize: 24, fontWeight: 'bold', color: 'var(--accent)', marginBottom: 4 }}>
                    {cleanText(totalClicks.toLocaleString())}
                  </div>
                  <Text variant="bodyMd" as="h3">{cleanText("Total Clicks")}</Text>
                  <Text variant="bodySm" color="subdued">{cleanText(`Last ${timeRange} hours`)}</Text>
                </div>
                <div style={{
                  padding: 20,
                  borderRadius: 12,
                  background: 'rgba(0, 168, 169, 0.1)',
                  border: '1px solid rgba(0, 168, 169, 0.3)'
                }}>
                  <div style={{ fontSize: 24, fontWeight: 'bold', color: 'var(--accent-2)', marginBottom: 4 }}>
                    {cleanText(sorted.length > 0 ? formatRegionName(sorted[0][0]) : '--')}
                  </div>
                  <Text variant="bodyMd" as="h3">{cleanText("Hottest Zone")}</Text>
                  <Text variant="bodySm" color="subdued">{cleanText("Most clicked area")}</Text>
                </div>
                <div style={{
                  padding: 20,
                  borderRadius: 12,
                  background: 'rgba(106, 13, 173, 0.1)',
                  border: '1px solid rgba(106, 13, 173, 0.3)'
                }}>
                  <div style={{ fontSize: 24, fontWeight: 'bold', color: 'var(--accent)', marginBottom: 4 }}>
                    {totalRageClicks}
                  </div>
                  <Text variant="bodyMd" as="h3">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      Total Rage Clicks
                      <Tooltip content="Rapid repeated clicks indicating user frustration (4+ clicks in 10 seconds)">
                        <Icon source={QuestionCircleIcon} color="subdued" />
                      </Tooltip>
                    </span>
                  </Text>
                  <Text variant="bodySm" color="subdued">Last 30 days</Text>
                </div>
              </div>
            </Card>
          </Layout.Section>

          {/* SECTION 2: FRICTION POINTS (Rage Clicks) + Navigation Insights */}
          <Layout.Section>
            <div className="grid grid-2">
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <h3 className="chart-title">{cleanText("Friction Points by Page")}</h3>
                  <Tooltip content="Pages with the most rage clicks - these need immediate attention">
                    <Icon source={QuestionCircleIcon} color="subdued" />
                  </Tooltip>
                </div>
                <div className="card--brand-purple" style={{ padding: 8 }}>
                  {rageChartData.length > 0 ? (
                    <div style={{ height: barChartHeight, width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={rageChartData}
                          margin={{ top: 10, right: 10, left: 10, bottom: 50 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="page"
                            interval={0}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                            fontSize={12}
                          />
                          <YAxis
                            label={{ value: 'Rage Clicks', angle: -90, position: 'insideLeft' }}
                            fontSize={12}
                          />
                          <RechartsTooltip content={<CustomTooltip />} />
                          <Bar dataKey="count" name="Rage Clicks">
                            {rageChartData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  entry.severity.level === 'critical' ? '#dc2626' :
                                  entry.severity.level === 'warning' ? '#f59e0b' :
                                  '#6A0DAD'
                                }
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div style={{ height: barChartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Text variant="bodyMd" color="subdued" alignment="center">
                        No rage click data available
                      </Text>
                    </div>
                  )}
                </div>
              </Card>

              {/* NEW: Navigation Insights Card (replaces pie chart) */}
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <h3 className="chart-title">{cleanText("Navigation Insights")}</h3>
                  <Tooltip content="AI-powered analysis of how users navigate your site - identifies confusion, dead ends, and friction points">
                    <Icon source={QuestionCircleIcon} color="subdued" />
                  </Tooltip>
                </div>
                <Box padding="4" className="card--brand-teal">
                  {insightsLoading ? (
                    <div style={{ height: barChartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Text variant="bodyMd" tone="subdued">Loading insights...</Text>
                    </div>
                  ) : insightsError ? (
                    <div style={{ height: barChartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Text variant="bodyMd" tone="subdued">Unable to load insights</Text>
                    </div>
                  ) : parsedBehavioralInsights ? (
                    <div style={{ 
                      padding: 20, 
                      minHeight: barChartHeight - 32,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      
                      {/* Issue Section */}
                      <div style={{ marginBottom: 20 }}>
                        <h4 style={{ 
                          marginBottom: 8,
                          fontSize: 14,
                          fontWeight: 700,
                          color: 'var(--accent)'
                        }}>
                          {cleanText("Issue")}
                        </h4>
                        {parsedBehavioralInsights.headline && (
                          <Text variant="headingMd" as="p" fontWeight="bold" style={{ marginBottom: 4 }}>
                            {cleanText(parsedBehavioralInsights.headline)}
                          </Text>
                        )}
                        {parsedBehavioralInsights.context && (
                          <Text variant="bodySm" tone="subdued" as="p">
                            {cleanText(parsedBehavioralInsights.context)}
                          </Text>
                        )}
                      </div>
                      
                      {/* Patterns Section */}
                      {parsedBehavioralInsights.stats.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                          <h4 style={{ 
                            marginBottom: 8,
                            fontSize: 14,
                            fontWeight: 700,
                            color: 'var(--accent)'
                          }}>
                            {cleanText("Patterns")}
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {parsedBehavioralInsights.stats.slice(0, 4).map((stat, idx) => (
                              <Text key={idx} variant="bodyMd" as="p">
                                {cleanText(stat)}
                              </Text>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Recommendations Section */}
                      {parsedBehavioralInsights.recommendations.length > 0 && (
                        <div>
                          <h4 style={{ 
                            marginBottom: 8,
                            fontSize: 14,
                            fontWeight: 700,
                            color: 'var(--accent-2)'
                          }}>
                            {cleanText("Recommendations")}
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {parsedBehavioralInsights.recommendations.slice(0, 3).map((rec, idx) => (
                              <Text key={idx} variant="bodyMd" as="p">
                                {idx + 1}. {cleanText(rec)}
                              </Text>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ height: barChartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Text variant="bodyMd" tone="subdued" alignment="center">
                        {cleanText("No navigation data yet. Check back after more visitors.")}
                      </Text>
                    </div>
                  )}
                </Box>
              </Card>
            </div>
          </Layout.Section>

          {/* SECTION 3: ENGAGEMENT HEATMAP */}
          <Layout.Section>
            <Card title={cleanText("Engagement Heatmap")} sectioned>
              {/* Filters Row */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 4, display: 'block' }}>
                    {cleanText("Show Data For")}
                  </label>
                  <select 
                    value={selectedPage} 
                    onChange={(e) => setSelectedPage(e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc' }}
                  >
                    <option value="">{cleanText("All Pages")}</option>
                    {pages.map((p, i) => {
                      const path = typeof p === 'string' ? p : p.page_path || p.path || '';
                      if (!path) return null;
                      return <option key={`${path}-${i}`} value={path}>{cleanText(path)}</option>;
                    })}
                  </select>
                </div>

                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 4, display: 'block' }}>
                    {cleanText("Time Range (hrs)")}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="168"
                    value={timeRange}
                    onChange={(e) => setTimeRange(Number(e.target.value))}
                    style={{ width: '100%' }}
                  />
                  <span style={{ fontSize: 12 }}>{cleanText(`${timeRange} hours`)}</span>
                </div>

                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 4, display: 'block' }}>
                    {cleanText("Visitors")}
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {['all', 'new', 'returning'].map(type => (
                      <label key={type} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input
                          type="radio"
                          name="userType"
                          value={type}
                          checked={userType === type}
                          onChange={(e) => setUserType(e.target.value)}
                        />
                        {cleanText(type.charAt(0).toUpperCase() + type.slice(1))}
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input 
                      type="checkbox" 
                      checked={showBg} 
                      onChange={e => setShowBg(e.target.checked)} 
                    />
                    {cleanText("Show background")}
                  </label>
                </div>
              </div>

              {/* Heatmap Visualization */}
              <Box padding="4" className="card--brand-teal" style={{ minHeight: 500 }}>
                {(selectedSite && showBg && !bgOk) && (
                  <Banner status="info" tone="info">
                    {cleanText("Loading page preview...")}
                  </Banner>
                )}
                {/* FIX: Updated overlay container for proper alignment */}
                <div 
                  className="heatmap-overlay" 
                  ref={overlayRef} 
                  style={{ 
                    minHeight: 500,
                    position: 'relative',
                    width: '100%',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                >
                  {/* Background screenshot */}
                  {(selectedSite && showBg) && (() => {
                    const siteHost = sessionStorage.getItem('site_host');
                    const qs = siteHost
                      ? `host=${encodeURIComponent(siteHost)}&path=${encodeURIComponent(normalizedPath || '/')}`
                      : `site_id=${encodeURIComponent(selectedSite)}&path=${encodeURIComponent(normalizedPath || '/')}`;
                    return (
                      <img
                        ref={bgImageRef}
                        className="heatmap-bg"
                        src={`${FN_BASE}/.netlify/functions/screenshot?${qs}&w=1200${screenshotDebug}&ts=${cacheBust}`}
                        alt=""
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain'
                        }}
                        onLoad={() => {
                          setBgOk(true);
                          // Redraw heatmap after image loads to align coordinates
                          setTimeout(() => redrawHeatmap(), 100);
                        }}
                        onError={(e) => {
                          setBgOk(false);
                          if (!isDev) e.currentTarget.style.display = 'none';
                        }}
                        loading="lazy"
                      />
                    );
                  })()}
                  
                  {/* Heatmap canvas - positioned to overlay the image */}
                  {gridData.length === 0 ? (
                    <Text variant="bodyMd" color="subdued">
                      {cleanText("No click data. Try 'All Pages' or widen the time range.")}
                    </Text>
                  ) : (
                    <>
                      <canvas
                        ref={canvasRef}
                        className="heatmap-canvas"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          pointerEvents: 'none',
                          mixBlendMode: 'multiply'
                        }}
                      />
                      {/* Region overlays for hover - positioned relative to canvas size */}
                      {REGIONS.map((r) => (
                        <div
                          key={r.key}
                          style={{
                            position: 'absolute',
                            top: `calc(50% - ${renderedImgSize.height / 2}px + ${parseFloat(r.top) / 100 * renderedImgSize.height}px)`,
                            left: `calc(50% - ${renderedImgSize.width / 2}px + ${parseFloat(r.left) / 100 * renderedImgSize.width}px)`,
                            width: renderedImgSize.width ? `${renderedImgSize.width / 3}px` : '33.333%',
                            height: renderedImgSize.height ? `${renderedImgSize.height / 3}px` : '33.333%',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={() => setHoverRegion(r.key)}
                          onMouseLeave={() => setHoverRegion(null)}
                        />
                      ))}
                      {hoverRegion && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: 'white',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            zIndex: 10
                          }}
                        >
                          <Text variant="bodyMd">
                            {cleanText(formatRegionName(hoverRegion))}
                            {regionCounts[hoverRegion] !== undefined
                              ? cleanText(` — ${regionCounts[hoverRegion]} clicks`)
                              : cleanText(' — No data')}
                          </Text>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span>{cleanText("Low")}</span>
                  <div style={{ width: 100, height: 10, background: 'linear-gradient(90deg, rgba(255,255,0,0.3), rgba(255,0,0,0.9))' }}></div>
                  <span>{cleanText("High")}</span>
                </div>
              </Box>

              {/* Click Distribution Summary */}
              {sorted.length > 0 && Object.keys(regionCounts).length > 0 && Math.max(...Object.values(regionCounts)) > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Text variant="headingMd" as="h4">{cleanText("Click Distribution")}</Text>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginTop: 12 }}>
                    {(() => {
                      const sortedTotal = sorted.reduce((sum, [, count]) => sum + count, 0);
                      return sorted.slice(0, 3).map(([region, clicks], idx) => {
                        const percentage = sortedTotal > 0 ? ((clicks / sortedTotal) * 100).toFixed(1) : '0.0';
                        return (
                          <div key={idx} style={{
                            padding: 12,
                            borderRadius: 8,
                            background: idx === 0 ? 'rgba(106, 13, 173, 0.1)' : 'rgba(0, 168, 169, 0.1)',
                            border: `1px solid ${idx === 0 ? 'rgba(106, 13, 173, 0.3)' : 'rgba(0, 168, 169, 0.3)'}`
                          }}>
                            <Text variant="bodyMd" fontWeight="semibold">{formatRegionName(region)}</Text>
                            <Text variant="bodyLg" fontWeight="bold">{`${clicks.toLocaleString()} clicks`}</Text>
                            <Text variant="bodySm" color="subdued">{`${percentage}% of total`}</Text>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </Card>
          </Layout.Section>

          {/* SECTION 4: PRIORITY FIXES */}
          {prioritizedIssues.length > 0 && (
            <Layout.Section>
              <Card title={cleanText("Priority Fixes")} sectioned>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {prioritizedIssues.slice(0, 3).map((issue, idx) => {
                    return (
                      <div key={idx} style={{
                        padding: 20,
                        borderRadius: 12,
                        background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.05), rgba(0, 168, 169, 0.05))',
                        border: issue.severity.label === 'Critical' ? '2px solid var(--accent)' : '1px solid rgba(106, 13, 173, 0.2)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                          <div>
                            <div style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: 6,
                              background: issue.severity.label === 'Critical' ? 'var(--accent)' : 'var(--accent-2)',
                              color: 'white',
                              fontSize: 12,
                              fontWeight: 'bold',
                              marginBottom: 8
                            }}>
                              {cleanText(`#${idx + 1} PRIORITY`)}
                            </div>
                            <Text variant="headingMd" fontWeight="semibold">{cleanText(formatPageLabel(issue.page_path))}</Text>
                          </div>
                        </div>
                        
                        <Text variant="bodyMd" tone="critical" as="p" style={{ marginBottom: 12 }}>
                          {cleanText(`${issue.count} rage clicks on a non-responsive ${describeElement(issue.element)}`)}
                        </Text>

                        <div style={{
                          padding: 12,
                          background: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: 8,
                          marginBottom: 12
                        }}>
                          <Text variant="bodySm" fontWeight="semibold">{cleanText("Why this happens:")}</Text>
                          <div style={{ marginTop: 4 }}>
                            <Text variant="bodySm">• The element is not clickable or is visually confusing.</Text>
                          </div>
                          <div>
                            <Text variant="bodySm">• The page is slow to load or respond to clicks.</Text>
                          </div>
                          <div>
                            <Text variant="bodySm">• The user expects a different outcome from their click.</Text>
                          </div>
                        </div>

                        <Text variant="bodySm" fontWeight="semibold">{cleanText("Quick Fixes:")}</Text>
                        <div style={{ marginTop: 8 }}>
                          <Text variant="bodySm">{cleanText("→ Check the element's code to ensure it's a valid link or button.")}</Text>
                          <span style={{
                            display: 'inline-block',
                            marginTop: 4,
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: 'rgba(0, 168, 169, 0.1)',
                            color: 'var(--accent-2)',
                            fontSize: 12,
                            fontWeight: 600
                          }}>
                            {cleanText("Reduce frustration")}
                          </span>
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <Text variant="bodySm">{cleanText("→ Implement visual feedback like a loading spinner or a hover state.")}</Text>
                          <span style={{
                            display: 'inline-block',
                            marginTop: 4,
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: 'rgba(0, 168, 169, 0.1)',
                            color: 'var(--accent-2)',
                            fontSize: 12,
                            fontWeight: 600
                          }}>
                            {cleanText("Improve user experience")}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </Layout.Section>
          )}

          {/* SECTION 5: HEATMAP INSIGHTS */}
          {sorted.length > 0 && Object.keys(regionCounts).length > 0 && Math.max(...Object.values(regionCounts)) > 0 && (
            <Layout.Section>
              <Card title={cleanText("Engagement Insights & Recommendations")} sectioned>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {/* Top performing regions */}
                  {(() => {
                    const sortedTotal = sorted.reduce((sum, [, count]) => sum + count, 0);
                    return sorted.slice(0, 3).map(([region, clicks], idx) => {
                      const percentage = sortedTotal > 0 ? ((clicks / sortedTotal) * 100).toFixed(1) : '0.0';

                      return (
                        <div key={idx} style={{
                          padding: 20,
                          borderRadius: 12,
                          background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.05), rgba(0, 168, 169, 0.05))',
                          border: idx === 0 ? '2px solid var(--accent)' : '1px solid rgba(106, 13, 173, 0.2)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                            <div>
                              <div style={{
                                display: 'inline-block',
                                padding: '4px 8px',
                                borderRadius: 6,
                                background: idx === 0 ? 'var(--accent)' : 'var(--accent-2)',
                                color: 'white',
                                fontSize: 12,
                                fontWeight: 'bold',
                                marginBottom: 8
                              }}>
                                {`#${idx + 1} ${idx === 0 ? 'HOTTEST ZONE' : 'HIGH ACTIVITY'}`}
                              </div>
                              <Text variant="headingMd" fontWeight="semibold">{formatRegionName(region)}</Text>
                            </div>
                          </div>

                          <Text variant="bodyMd" tone={idx === 0 ? "success" : "subdued"} as="p" style={{ marginBottom: 12 }}>
                            {`${clicks.toLocaleString()} clicks (${percentage}% of total activity)`}
                          </Text>

                          <Text variant="bodySm" fontWeight="semibold">{cleanText("Recommended Actions:")}</Text>
                          {idx === 0 && (
                            <>
                              <div style={{ marginTop: 8 }}>
                                <Text variant="bodySm">{`→ Keep your most important CTAs in ${formatRegionName(region)}`}</Text>
                              <span style={{
                                display: 'inline-block',
                                marginTop: 4,
                                padding: '2px 8px',
                                borderRadius: 6,
                                background: 'rgba(0, 168, 169, 0.1)',
                                color: 'var(--accent-2)',
                                fontSize: 12,
                                fontWeight: 600
                              }}>
                                {cleanText("High impact")}
                              </span>
                            </div>
                            <div style={{ marginTop: 8 }}>
                              <Text variant="bodySm">{cleanText("→ Test special offers or promotions here")}</Text>
                              <span style={{
                                display: 'inline-block',
                                marginTop: 4,
                                padding: '2px 8px',
                                borderRadius: 6,
                                background: 'rgba(0, 168, 169, 0.1)',
                                color: 'var(--accent-2)',
                                fontSize: 12,
                                fontWeight: 600
                              }}>
                                {cleanText("Quick win")}
                              </span>
                            </div>
                          </>
                        )}
                        {idx === 1 && (
                          <div style={{ marginTop: 8 }}>
                            <Text variant="bodySm">{cleanText("→ Use this area for secondary actions or social proof")}</Text>
                            <span style={{
                              display: 'inline-block',
                              marginTop: 4,
                              padding: '2px 8px',
                              borderRadius: 6,
                              background: 'rgba(0, 168, 169, 0.1)',
                              color: 'var(--accent-2)',
                              fontSize: 12,
                              fontWeight: 600
                            }}>
                              {cleanText("Medium impact")}
                            </span>
                          </div>
                        )}
                        {idx === 2 && (
                          <div style={{ marginTop: 8 }}>
                            <Text variant="bodySm">{cleanText("→ Consider this zone for trust badges or testimonials")}</Text>
                            <span style={{
                              display: 'inline-block',
                              marginTop: 4,
                              padding: '2px 8px',
                              borderRadius: 6,
                              background: 'rgba(0, 168, 169, 0.1)',
                              color: 'var(--accent-2)',
                              fontSize: 12,
                              fontWeight: 600
                            }}>
                              {cleanText("Build trust")}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  });
                  })()}

                  {/* Quick Wins Checklist */}
                  <div style={{
                    padding: 20,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.05), rgba(0, 168, 169, 0.05))',
                    border: '1px solid rgba(106, 13, 173, 0.2)'
                  }}>
                    <Text variant="headingMd" fontWeight="semibold">{cleanText("Quick Wins Checklist")}</Text>
                    <Text variant="bodySm" color="subdued" as="p" style={{ marginBottom: 12 }}>
                      {cleanText("Simple changes that boost conversions:")}
                    </Text>
                    {quickWins.slice(0, 5).map((win) => (
                      <div key={win.id} style={{ marginTop: 8 }}>
                        <Text variant="bodySm">• {win.label}</Text>
                        <span style={{
                          marginLeft: 8,
                          padding: '2px 6px',
                          borderRadius: 4,
                          background: win.impact === 'high' ? 'rgba(106, 13, 173, 0.1)' : 'rgba(0, 168, 169, 0.1)',
                          color: win.impact === 'high' ? 'var(--accent)' : 'var(--accent-2)',
                          fontSize: 11,
                          fontWeight: 600
                        }}>
                          {cleanText(`${win.impact} impact`)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Industry Benchmarks */}
                  <div style={{
                    padding: 20,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.05), rgba(0, 168, 169, 0.05))',
                    border: '1px solid rgba(106, 13, 173, 0.2)'
                  }}>
                    <Text variant="headingMd" fontWeight="semibold">{cleanText("Industry Benchmarks")}</Text>
                    <Text variant="bodySm" color="subdued" as="p" style={{ marginBottom: 12 }}>
                      {cleanText("How you compare to similar businesses:")}
                    </Text>
                    {Object.entries(benchmarkData.benchmarks).map(([industry, data]) => (
                      <div key={industry} style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <Text variant="bodyMd">{cleanText(industry)}</Text>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {benchmarkData.topPercent > data.topRegion ? (
                              <>
                                <Icon source={CheckIcon} color="success" />
                                <Text variant="bodySm" tone="success">
                                  {cleanText('Above avg')}
                                </Text>
                              </>
                            ) : (
                              <>
                                <Icon source={ChartHistogramFullIcon} color="warning" />
                                <Text variant="bodySm" tone="warning">
                                  {cleanText('Below avg')}
                                </Text>
                              </>
                            )}
                          </div>
                        </div>
                        <div style={{
                          height: 8,
                          background: '#e5e7eb',
                          borderRadius: 6,
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          <div
                            style={{
                              width: `${benchmarkData.topPercent}%`,
                              height: '100%',
                              background: benchmarkData.topPercent > data.topRegion ? 'var(--accent-2)' : 'var(--accent)'
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              left: `${data.topRegion}%`,
                              top: 0,
                              width: 2,
                              height: '100%',
                              background: 'black',
                              opacity: 0.3
                            }}
                            title={cleanText(`Industry avg: ${data.topRegion}%`)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </Layout.Section>
          )}
        </Layout>
      </Page>
    </LayoutWrapper>
  );
}